import React, { useCallback, useEffect, useRef } from 'react'
import { BottomSheetBackdrop, BottomSheetModal, BottomSheetView } from '@gorhom/bottom-sheet'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { ReduceMotion } from 'react-native-reanimated'
import { DS } from '@/design-system'
import { teacherControlStyles as styles } from './styles'
import { AddBotsContent } from './AddBotsContent'
import type { BotSkillLevel } from '@ppwr/poker-engine/utils'
import type { BotSkillCounts } from './types'

type AddBotsBottomSheetProps = {
  isOpen: boolean
  availableSeats: number
  isAddingDuringGame: boolean
  skillCounts: BotSkillCounts
  isSubmitting?: boolean
  onClose: () => void
  onConfirm: () => void
  onIncrement: (skillLevel: BotSkillLevel) => void
  onDecrement: (skillLevel: BotSkillLevel) => void
}

export function AddBotsBottomSheet({
  isOpen,
  availableSeats,
  isAddingDuringGame,
  skillCounts,
  isSubmitting = false,
  onClose,
  onConfirm,
  onIncrement,
  onDecrement
}: AddBotsBottomSheetProps) {
  const insets = useSafeAreaInsets()
  const bottomSheetRef = useRef<BottomSheetModal>(null)

  useEffect(() => {
    if (isOpen) {
      bottomSheetRef.current?.present()
    } else {
      bottomSheetRef.current?.dismiss()
    }
  }, [isOpen])

  const renderBackdrop = useCallback((props: React.ComponentProps<typeof BottomSheetBackdrop>) => {
    return (
      <BottomSheetBackdrop
        {...props}
        disappearsOnIndex={-1}
        appearsOnIndex={0}
        opacity={0.25}
        pressBehavior={isSubmitting ? 'none' : 'close'}
      />
    )
  }, [isSubmitting])

  return (
    <BottomSheetModal
      ref={bottomSheetRef}
      onDismiss={onClose}
      overrideReduceMotion={ReduceMotion.System}
      enableDynamicSizing={true}
      enablePanDownToClose={!isSubmitting}
      enableOverDrag={false}
      backdropComponent={renderBackdrop}
      handleIndicatorStyle={{ backgroundColor: '#0003' }}
      backgroundStyle={{ backgroundColor: DS.palette['Brand/Cream'] }}
    >
      <BottomSheetView style={[styles.sheetContainer, { paddingBottom: insets.bottom || 20 }]}>
        <AddBotsContent
          availableSeats={availableSeats}
          isAddingDuringGame={isAddingDuringGame}
          skillCounts={skillCounts}
          isSubmitting={isSubmitting}
          onClose={onClose}
          onConfirm={onConfirm}
          onIncrement={onIncrement}
          onDecrement={onDecrement}
        />
      </BottomSheetView>
    </BottomSheetModal>
  )
}
